"use client";

import React, { useEffect, useState } from "react";
import { Flag, MoreVertical } from "lucide-react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/Components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/Components/ui/dropdown-menu";
const REPORT_REASONS = [{
  value: "spam_or_advertising",
  label: "Spam or advertising",
  description: "Review is off-topic, promotional, repetitive, or spam."
}, {
  value: "offensive_or_abusive",
  label: "Offensive or abusive content",
  description: "Review contains abusive, threatening, profane, or harassing language."
}, {
  value: "misleading_medical_information",
  label: "Misleading medical information",
  description: "Review includes inaccurate or potentially unsafe medical claims."
}, {
  value: "conflict_of_interest",
  label: "Conflict of interest",
  description: "Reviewer may have an undisclosed personal or commercial interest."
}, {
  value: "personal_or_private_information",
  label: "Personal or private information",
  description: "Review shares private information, such as a name, address, or phone number."
}, {
  value: "other",
  label: "Other",
  description: "Another issue that is not covered by the reasons above."
}];
const getJwt = () => {
  const tokenCookie = document.cookie.split(";").map(cookie => cookie.trim()).find(cookie => cookie.startsWith("token="));
  return tokenCookie ? `Bearer ${tokenCookie.slice("token=".length)}` : null;
};
const ReportReviewDialog = ({
  reviewId
}) => {
  const user = useSelector(state => state?.userData?.userData?.user);
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [reported, setReported] = useState(false);
  useEffect(() => {
    if (!user || !reviewId) {
      setReported(false);
      return;
    }
    const jwt = getJwt();
    if (!jwt) return;
    const controller = new AbortController();
    const checkReportStatus = async () => {
      try {
        const response = await fetch(`${process.env.NEXT_PUBLIC_BASE_URL}/review-reports/${reviewId}/status`, {
          headers: {
            Authorization: jwt
          },
          signal: controller.signal
        });
        if (!response.ok) return;
        const body = await response.json();
        setReported(Boolean(body?.reported));
      } catch (error) {
        if (error?.name !== "AbortError") {
          console.error("Unable to check review report status:", error);
        }
      }
    };
    checkReportStatus();
    return () => controller.abort();
  }, [reviewId, user]);
  const openReport = () => {
    if (!user) {
      toast.info("Please log in to report a review.");
      return;
    }
    if (!reviewId) {
      toast.error("This review cannot be reported right now.");
      return;
    }
    setOpen(true);
  };
  const closeAndReset = nextOpen => {
    if (submitting) return;
    setOpen(nextOpen);
    if (!nextOpen) {
      setReason("");
      setDetails("");
    }
  };
  const submitReport = async () => {
    if (!reason || submitting) return;
    const jwt_0 = getJwt();
    if (!jwt_0) {
      toast.error("Your session has expired. Please log in again.");
      return;
    }
    setSubmitting(true);
    try {
      const response_0 = await fetch(`${process.env.NEXT_PUBLIC_BASE_URL}/review-reports`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: jwt_0
        },
        body: JSON.stringify({
          reviewId,
          reason,
          details: details.trim()
        })
      });
      const body_0 = await response_0.json();
      if (!response_0.ok) {
        if (response_0.status === 409) {
          setReported(true);
          setOpen(false);
          toast.info(body_0?.message || "You have already reported this review.");
          return;
        }
        throw new Error(body_0?.message || "Unable to submit the report.");
      }
      setReported(true);
      setOpen(false);
      setReason("");
      setDetails("");
      toast.success(body_0?.message || "Thanks for reporting this review. Our team will review it.");
    } catch (error_0) {
      toast.error(error_0?.message || "Unable to submit the report.");
    } finally {
      setSubmitting(false);
    }
  };
  return <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button type="button" aria-label="Review options" className="mt-2 inline-flex h-8 w-8 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-primaryBlue/30">
            <MoreVertical className="h-4 w-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          <DropdownMenuItem disabled={reported} onSelect={openReport} className="cursor-pointer gap-2 text-sm">
            <Flag className="h-4 w-4" />
            {reported ? "Already reported" : "Report review"}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={open} onOpenChange={closeAndReset}>
        <DialogContent className="flex max-h-[90vh] w-[calc(100%-2rem)] max-w-2xl grid-cols-none flex-col gap-0 overflow-hidden rounded-2xl bg-white p-0">
          <DialogHeader className="shrink-0 border-b border-gray-200 px-6 py-2 pr-12 sm:px-8 sm:py-4 sm:pr-14">
            <DialogTitle className="font-poppins text-2xl">
              Report this review
            </DialogTitle>
            <DialogDescription className="pt-1 font-poppins text-xs leading-relaxed">
              Tell us why this review may violate our guidelines. Reporting does
              not remove it automatically.
            </DialogDescription>
          </DialogHeader>

          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5 sm:px-8 sm:py-6">
            <div className="space-y-3">
              {REPORT_REASONS.map(item => <button type="button" key={item.value} aria-pressed={reason === item.value} onClick={() => setReason(item.value)} className={`flex w-full items-center justify-between gap-4 rounded-xl border-2 px-5 py-4 text-left font-poppins transition-all ${reason === item.value ? "border-primary-primaryBlue bg-blue-50 shadow-sm" : "border-gray-200 bg-white hover:border-blue-300 hover:shadow-sm"}`}>
                  <span className="min-w-0">
                    <span className={`block text-base font-medium ${reason === item.value ? "text-primary-primaryBlue" : "text-gray-900"}`}>
                      {item.label}
                    </span>
                    <span className="mt-1 block text-sm leading-relaxed text-gray-500">
                      {item.description}
                    </span>
                  </span>
                </button>)}
            </div>

            <div className="mt-6">
              <label htmlFor={`report-details-${reviewId}`} className="mb-2 block font-poppins text-sm font-semibold text-gray-800">
                Additional details{" "}
                <span className="font-normal text-gray-400">(optional)</span>
              </label>
              <textarea id={`report-details-${reviewId}`} value={details} onChange={event => setDetails(event.target.value)} maxLength={500} rows={4} placeholder="Add information that may help our team review this." className="w-full resize-none rounded-xl border border-gray-300 px-4 py-3 font-poppins text-sm outline-none focus:border-primary-primaryBlue focus:ring-2 focus:ring-primary-primaryBlue/10" />
              <div className="mt-1 text-right text-xs text-gray-400">
                {details.length}/500
              </div>
            </div>
          </div>

          <DialogFooter className="shrink-0 gap-3 border-t border-gray-200 bg-white px-6 py-4 sm:flex-row sm:px-8 sm:py-5 sm:space-x-0">
            <button type="button" onClick={() => closeAndReset(false)} disabled={submitting} className="w-full rounded-xl border-2 border-gray-300 px-5 py-3 font-poppins text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 sm:flex-1">
              Cancel
            </button>
            <button type="button" onClick={submitReport} disabled={!reason || submitting} className="w-full rounded-xl bg-primary-primaryBlue px-5 py-3 font-poppins text-sm font-semibold text-white hover:bg-primary-primaryBlue/90 disabled:cursor-not-allowed disabled:opacity-50 sm:flex-1">
              {submitting ? "Submitting..." : "Submit report"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>;
};
export default ReportReviewDialog;